import { useState, useEffect, useCallback } from 'react';
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { ParceiroDetailedMetrics, ParceiroMetrics, ParceiroFilters as ParceiroFiltersType } from '@/types/parceiros';

export interface ParceiroSimples {
  id: string;
  nome: string;
  email?: string;
  cidade?: string;
  estado?: string;
  ativo: boolean;
  vendedor_id: string;
}

interface Estado {
  sigla: string;
  nome: string;
}

interface Cidade {
  id: number;
  municipio: string;
  uf: string;
}

/**
 * Hook para buscar lista de parceiros com métricas completas (usado na página de gerenciamento)
 * @param filters - Filtros para buscar parceiros
 */
export function useParceiros(filters: ParceiroFiltersType): {
  parceiros: ParceiroMetrics[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
};

/**
 * Hook para buscar lista simples de parceiros (usado em selects)
 * @param vendedorId - ID do vendedor para filtrar os parceiros
 */
export function useParceiros(vendedorId?: string): {
  parceiros: ParceiroSimples[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
};

/**
 * Implementação do hook useParceiros com sobrecarga
 */
export function useParceiros(filterOrVendedorId?: string | ParceiroFiltersType) {
  const [parceiros, setParceiros] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Detectar se é filtro (objeto) ou vendedorId (string)
  const isFilter = typeof filterOrVendedorId === 'object' && filterOrVendedorId !== null;
  const vendedorId = typeof filterOrVendedorId === 'string' ? filterOrVendedorId : undefined;
  const filters = isFilter ? filterOrVendedorId : undefined;

  const fetchParceiros = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let url: string;
      
      if (isFilter && filters) {
        // Buscar parceiros com métricas completas (página de gerenciamento)
        url = '/api/parceiros';
      } else {
        // Buscar parceiros simples (para selects)
        url = vendedorId 
          ? `/api/parceiros/simples?vendedor_id=${vendedorId}`
          : '/api/parceiros/simples';
      }

      const response = await fetchWithAuth(url, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao buscar parceiros');
      }

      const data = await response.json();

      if (import.meta.env.DEV) {
        console.log('🔍 useParceiros - Parceiros carregados:', data);
      }

      setParceiros(data);
    } catch (err) {
      console.error('Erro ao buscar parceiros:', err);
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
      setParceiros([]);
    } finally {
      setLoading(false);
    }
  }, [isFilter, vendedorId, filters]);

  useEffect(() => {
    fetchParceiros();
  }, [fetchParceiros]);

  return { parceiros, loading, error, refetch: fetchParceiros };
}

/**
 * Hook para buscar lista de estados brasileiros
 */
export function useEstados() {
  const [estados, setEstados] = useState<Estado[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEstados = async () => {
      try {
        setLoading(true);
        const response = await fetchWithAuth('/api/parceiros/estados');

        if (!response.ok) {
          throw new Error('Erro ao buscar estados');
        }

        const data = await response.json();
        setEstados(data);
        setError(null);
      } catch (err) {
        console.error('Erro ao buscar estados:', err);
        setError(err instanceof Error ? err.message : 'Erro desconhecido');
      } finally {
        setLoading(false);
      }
    };

    fetchEstados();
  }, []);

  return { estados, loading, error };
}

/**
 * Hook para buscar cidades de um estado específico
 * @param uf - Sigla do estado (ex: SP, RJ, MG)
 */
export function useCidades(uf?: string) {
  const [cidades, setCidades] = useState<Cidade[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!uf) {
      setCidades([]);
      return;
    }

    const fetchCidades = async () => {
      try {
        setLoading(true);
        const response = await fetchWithAuth(`/api/parceiros/cidades/${uf}`);

        if (!response.ok) {
          throw new Error('Erro ao buscar cidades');
        }

        const data = await response.json();
        setCidades(data);
        setError(null);
      } catch (err) {
        console.error('Erro ao buscar cidades:', err);
        setError(err instanceof Error ? err.message : 'Erro desconhecido');
        setCidades([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCidades();
  }, [uf]);

  return { cidades, loading, error };
}

/**
 * Hook para cadastrar um novo parceiro
 */
export function useCadastrarParceiro() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const cadastrar = useCallback(async (data: {
    nome: string;
    email: string;
    telefone: string;
    cidade: string;
    estado: string;
    cep?: string;
    endereco?: string;
    numero?: string;
    bairro?: string;
    cpf?: string;
    pix?: string;
    vendedor_id?: string;
    pode_criar_rede_parceiros?: boolean;
    tipo_pessoa?: 'PF' | 'PJ';
    cnpj?: string;
    razao_social?: string;
    nome_fantasia?: string;
    cpf_representante?: string;
  }) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      const response = await fetchWithAuth('/api/parceiros', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        let errorMessage = 'Erro ao cadastrar parceiro';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (parseError) {
          // Se não conseguir fazer parse do JSON, usar status text
          errorMessage = response.statusText || `Erro ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      setSuccess(true);
      try {
        return await response.json();
      } catch (parseError) {
        // Se a resposta não for JSON válido, ainda considera sucesso
        console.warn('Resposta não é JSON válido, mas status foi OK');
        return { message: 'Parceiro cadastrado com sucesso' };
      }
    } catch (err) {
      console.error('Erro ao cadastrar parceiro:', err);
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setError(null);
    setSuccess(false);
  }, []);

  return { cadastrar, loading, error, success, reset };
}

/**
 * Hook para buscar detalhes completos de um parceiro específico
 * @param parceiroId - ID do parceiro
 */
export function useParceiroDetails(parceiroId: string | null) {
  const [parceiro, setParceiro] = useState<ParceiroDetailedMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchParceiro = useCallback(async () => {
    if (!parceiroId) {
      setParceiro(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetchWithAuth(`/api/parceiros/${parceiroId}/metrics`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao buscar detalhes do parceiro');
      }

      const data: ParceiroDetailedMetrics = await response.json();
      console.log('🔍 useParceiroDetails - dados recebidos:', data);
      console.log('🔍 useParceiroDetails - parceiro_id:', data.parceiro_id, 'type:', typeof data.parceiro_id);
      setParceiro(data);
    } catch (err) {
      console.error('Erro ao buscar detalhes do parceiro:', err);
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
      setParceiro(null);
    } finally {
      setLoading(false);
    }
  }, [parceiroId]);

  useEffect(() => {
    fetchParceiro();
  }, [fetchParceiro]);

  return { parceiro, loading, error, refetch: fetchParceiro };
}
